// Error notification utility that sends emails to support@smartmetrics.com
// This is a simple, free alternative to paid error tracking services
// Designed to be easily replaceable with Sentry later

// Import structured logger for real-time monitoring
import { logger } from './logger'
import { serializeUnknown } from './error-details'

export type ErrorSeverity = 'error' | 'warning' | 'info'

export interface ErrorContext {
  user?: {
    id?: string
    email?: string
    role?: string
  }
  request?: {
    url?: string
    method?: string
    headers?: Record<string, string>
    body?: any
  }
  api?: {
    endpoint?: string
    statusCode?: number
    responseTime?: number
  }
  external?: {
    service?: string
    url?: string
    statusCode?: number
  }
  custom?: Record<string, any>
  stack?: string
  timestamp?: string
}

// Send error notification email to support
async function sendErrorEmail(
  error: Error | string,
  context?: ErrorContext,
  severity: ErrorSeverity = 'error'
) {
  // Always log to structured logger for real-time monitoring
  const errorMessage = typeof error === 'string' ? error : error.message
  const errorStack = error instanceof Error ? error.stack : undefined
  
  logger.error(errorMessage, {
    errorType: error instanceof Error ? error.name : 'UnknownError',
    stack: errorStack,
    severity,
    ...context
  })

  // Only send emails in production
  if (process.env.NODE_ENV !== 'production') {
    return
  }

  try {
    const timestamp = context?.timestamp || new Date().toISOString()
    
    const emailBody = `
🚨 Production Error Alert

Error: ${errorMessage}
Severity: ${severity.toUpperCase()}
Timestamp: ${timestamp}
Environment: ${process.env.NODE_ENV}

${errorStack ? `Stack Trace:\n${errorStack}\n` : ''}

Context:
${context?.user ? `User: ${context.user.id || 'unknown'} (${context.user.email || 'no email'}) [${context.user.role || 'unknown role'}]\n` : ''}
${context?.request ? `Request: ${context.request.method} ${context.request.url}\n` : ''}
${context?.api ? `API Endpoint: ${context.api.endpoint} (Status: ${context.api.statusCode}, Time: ${context.api.responseTime}ms)\n` : ''}
${context?.external ? `External Service: ${context.external.service} (${context.external.url}) [Status: ${context.external.statusCode}]\n` : ''}
${context?.custom ? `Additional Info: ${JSON.stringify(context.custom, null, 2)}\n` : ''}

This error occurred in production and requires attention.
    `.trim()

    // Use the built-in email API route
    const response = await fetch('/api/error-notification', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subject: `🚨 Production Error: ${errorMessage}`,
        body: emailBody,
        severity,
        error: errorMessage,
        stack: errorStack,
        context
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      logger.error('Failed to send error notification email', {
        errorType: 'EmailSendError',
        emailError: errorText
      })
    } else {
      logger.info('Error notification email sent successfully', {
        errorType: errorMessage
      })
    }
  } catch (emailError) {
    logger.error('Failed to send error notification email', {
      errorType: 'EmailSendError',
      stack: emailError instanceof Error ? emailError.stack : undefined
    })
  }
}

// Report error with context
export function reportError(
  error: Error | string,
  context?: ErrorContext,
  severity: ErrorSeverity = 'error'
) {
  try {
    if (typeof error === 'string') {
      error = new Error(error)
    }

    // Send error notification email (also logs via structured logger)
    sendErrorEmail(error, context, severity)
  } catch (reportError) {
    logger.error('Failed to report error', {
      errorType: 'ErrorReportingError',
      stack: reportError instanceof Error ? reportError.stack : undefined,
      originalError: error instanceof Error ? error.message : String(error)
    })
  }
}

// Report API errors with request context
export function reportApiError(
  error: Error | string,
  request?: Request,
  context?: Record<string, any>
) {
  const apiContext: ErrorContext = {
    request: request ? {
      url: request.url,
      method: request.method,
      headers: Object.fromEntries(request.headers.entries())
    } : undefined,
    custom: context,
    timestamp: new Date().toISOString()
  }

  reportError(error, apiContext, 'error')
}

// Report API errors with detailed context
export function reportDetailedApiError(
  error: Error | string,
  request?: Request,
  apiContext?: {
    endpoint?: string
    statusCode?: number
    responseTime?: number
  },
  userContext?: {
    id?: string
    email?: string
    role?: string
  },
  additionalContext?: Record<string, any>
) {
  const context: ErrorContext = {
    user: userContext,
    request: request ? {
      url: request.url,
      method: request.method,
      headers: Object.fromEntries(request.headers.entries())
    } : undefined,
    api: apiContext,
    custom: additionalContext,
    timestamp: new Date().toISOString()
  }

  reportError(error, context, 'error')
}

// Report OpenAI errors specifically
export function reportOpenAIError(
  error: Error | string,
  model?: string,
  context?: Record<string, any>
) {
  const openAIContext: ErrorContext = {
    external: {
      service: 'openai',
      url: 'https://api.openai.com'
    },
    custom: {
      model,
      ...context
    },
    timestamp: new Date().toISOString()
  }

  reportError(error, openAIContext, 'error')
}

// Report external API errors
export function reportExternalApiError(
  error: Error | string,
  service: string,
  url: string,
  statusCode?: number,
  context?: Record<string, any>
) {
  const externalContext: ErrorContext = {
    external: {
      service,
      url,
      statusCode
    },
    custom: context,
    timestamp: new Date().toISOString()
  }

  reportError(error, externalContext, 'error')
}

// Report client-side errors
export function reportClientError(
  error: Error | string,
  component?: string,
  context?: Record<string, any>
) {
  // Filter out known non-critical browser warnings
  const serialized = serializeUnknown(error)
  if (serialized.skip) {
    console.warn('Non-critical browser warning:', serialized.message)
    return
  }

  const clientContext: ErrorContext = {
    custom: {
      environment: 'client',
      component,
      serializedMessage: serialized.message,
      serializedDetails: serialized.details || undefined,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      ...context
    },
    timestamp: new Date().toISOString()
  }

  reportError(error, clientContext, 'error')
}

// Report authentication errors
export function reportAuthError(
  error: Error | string,
  authType: 'signup' | 'signin' | 'signout' | 'google' | 'api_key' | 'jwt',
  userContext?: {
    id?: string
    email?: string
    role?: string
  },
  additionalContext?: Record<string, any>
) {
  const authContext: ErrorContext = {
    user: userContext,
    custom: {
      authType,
      ...additionalContext
    },
    timestamp: new Date().toISOString()
  }

  reportError(error, authContext, 'error')
}

// Report database errors
export function reportDatabaseError(
  error: Error | string,
  operation: string,
  table?: string,
  context?: Record<string, any>
) {
  const dbContext: ErrorContext = {
    custom: {
      service: 'database',
      operation,
      table,
      ...context
    },
    timestamp: new Date().toISOString()
  }

  reportError(error, dbContext, 'error')
}

// Report performance issues
export function reportPerformanceIssue(
  issue: string,
  duration?: number,
  context?: Record<string, any>
) {
  const performanceContext: ErrorContext = {
    custom: {
      type: 'performance',
      duration,
      ...context
    }
  }

  reportError(new Error(issue), performanceContext, 'warning')
}

// Wrapper for async functions with error reporting
export function withErrorReporting<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  context?: Record<string, any>
): (...args: T) => Promise<R> {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args)
    } catch (error) {
      reportError(error as Error, { custom: context }, 'error')
      throw error
    }
  }
}

// Wrapper for sync functions with error reporting
export function withSyncErrorReporting<T extends any[], R>(
  fn: (...args: T) => R,
  context?: Record<string, any>
): (...args: T) => R {
  return (...args: T): R => {
    try {
      return fn(...args)
    } catch (error) {
      reportError(error as Error, { custom: context }, 'error')
      throw error
    }
  }
}

// Track API calls with error reporting
export async function trackApiCall<T>(
  apiCall: () => Promise<T>,
  endpoint: string,
  userContext?: {
    id?: string
    email?: string
    role?: string
  }
): Promise<T> {
  const startTime = Date.now()
  
  try {
    const result = await apiCall()
    const responseTime = Date.now() - startTime
    
    // Log successful API calls for monitoring

    
    return result
  } catch (error) {
    const responseTime = Date.now() - startTime
    
    reportDetailedApiError(
      error as Error,
      undefined,
      {
        endpoint,
        statusCode: 500,
        responseTime
      },
      userContext
    )
    
    throw error
  }
}

// Track external API calls with error reporting
export async function trackExternalApiCall<T>(
  apiCall: () => Promise<T>,
  service: string,
  url: string,
  context?: Record<string, any>
): Promise<T> {
  const startTime = Date.now()
  
  try {
    const result = await apiCall()
    const responseTime = Date.now() - startTime
    
    // Log successful external API calls

    
    return result
  } catch (error) {
    const responseTime = Date.now() - startTime
    
    reportExternalApiError(
      error as Error,
      service,
      url,
      undefined,
      {
        responseTime,
        ...context
      }
    )
    
    throw error
  }
}

// Extend global to prevent duplicate handlers on hot reload
declare global {
  var __errorHandlersRegistered: boolean | undefined;
}

// Global error handler for unhandled promise rejections
export function setupGlobalErrorHandlers() {
  // Prevent duplicate handlers during hot module reloading
  if (global.__errorHandlersRegistered) {
    return;
  }
  
  if (typeof window !== 'undefined') {
    // Client-side error handlers
    window.addEventListener('error', (event) => {
      const errorMessage = event.error?.message || event.message || '';
      
      // Skip ResizeObserver and other non-critical browser warnings and browser extension errors
      if (errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
          errorMessage.includes('Script error') ||
          errorMessage.includes('Loading chunk') ||
          errorMessage.includes('Loading CSS chunk') ||
          errorMessage.includes('Object Not Found Matching Id') ||
          errorMessage.includes('MethodName:update') ||
          errorMessage.includes('Socket server did not execute the callback')) {
        console.warn('Non-critical browser warning (filtered):', errorMessage);
        return;
      }
      
      reportClientError(
        event.error || event.message,
        'global_error_handler',
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      )
    })

    window.addEventListener('unhandledrejection', (event) => {
      const errorMessage = event.reason instanceof Error ? event.reason.message : String(event.reason);
      
      // Skip non-critical promise rejections and browser extension errors
      if (errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
          errorMessage.includes('Non-Error promise rejection captured') ||
          errorMessage.includes('Script error') ||
          errorMessage.includes('Object Not Found Matching Id') ||
          errorMessage.includes('MethodName:update') ||
          errorMessage.includes('MetaMask') ||
          errorMessage.includes('Failed to connect to MetaMask') ||
          errorMessage.includes('Socket server did not execute the callback')) {
        console.warn('Non-critical promise rejection (filtered):', errorMessage);
        return;
      }
      
      reportClientError(
        event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        'unhandled_promise_rejection',
        {
          type: typeof event.reason
        }
      )
    })
  } else {
    // Server-side error handlers
    process.on('unhandledRejection', (reason, promise) => {
      reportError(
        reason instanceof Error ? reason : new Error(String(reason)),
        {
          custom: {
            type: 'unhandled_promise_rejection',
            promise: promise.toString()
          },
          timestamp: new Date().toISOString()
        },
        'error'
      )
    })

    process.on('uncaughtException', (error) => {
      reportError(
        error,
        {
          custom: {
            type: 'uncaught_exception'
          },
          timestamp: new Date().toISOString()
        },
        'error'
      )
    })
  }
  
  // Mark as registered (persists across hot reloads)
  global.__errorHandlersRegistered = true;
}
